import { Component, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { locale as en } from './../i18n/en';
import { locale as zh } from './../i18n/zh';

import { Router } from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';
import { JWTService } from '@app/core/services/jwt.service';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, debounceTime, tap } from 'rxjs/operators';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class LoginComponent implements OnInit, OnDestroy {
    environment = environment;
    form: FormGroup;

    buttonOption: MatProgressButtonOptions = {
        active: false,
        text: '',
        buttonColor: 'accent',
        barColor: 'accent',
        raised: true,
        stroked: false,
        mode: 'indeterminate',
        value: 0,
        disabled: false
    };

    translations: string[];

    private unsubscribeAll: Subject<any>;

    constructor(
        private fuseConfigService: FuseConfigService,
        private formBuilder: FormBuilder,
        private fuseTranslationLoaderService: FuseTranslationLoaderService,
        private router: Router,
        private translateService: TranslateService,
        private authService: AuthService,
        private jwtService: JWTService,
        public snackBar: MatSnackBar
    ) {
        // Configure the layout
        this.fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                },
                footer: {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };

        this.fuseTranslationLoaderService.loadTranslations(en, zh);
        this.unsubscribeAll = new Subject();

        this.translateService
            .get([
                'auth.login.login_button'
            ])
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(translations => {
                this.translations = translations;
                this.buttonOption.text = translations['auth.login.login_button'];
            });

        this.form = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            remember_me: [this.jwtService.rememberMe]
        });
    }

    ngOnInit(): void {
        this.form.valueChanges
            .pipe(
                takeUntil(this.unsubscribeAll),
                debounceTime(150),
                tap(
                    () =>
                        (this.buttonOption.disabled =
                            this.form.pristine ||
                            this.form.invalid)
                )
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.snackBar.dismiss();

        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    login(): void {
        if (!this.buttonOption.disabled && !this.buttonOption.active) {
            this.buttonOption.active = true;

            const { email, password, remember_me } = this.form.value;

            this.authService.login(email, password, remember_me).subscribe(
                data => {
                    this.router.navigate(['/dashboard'], { replaceUrl: true });
                },
                error => this.showErrorMessage(error)
            );
        }
    }

    private showErrorMessage(error: any): void {
        if (error.httpError.error && error.httpError.error.is_verify) {
            const snackBarRef = this.snackBar.open('Check Email to Activate Account', 'Resend Activation Link');

            snackBarRef.onAction().subscribe(() => {
                this.resendConfirmationEmail();
            });

        } else {
            this.snackBar.open(error.parsedMessage, 'Close', {
                panelClass: ['warn']
            });
        }

        this.buttonOption.active = false;
    }

    resendConfirmationEmail(): void {
        this.buttonOption.text = 'Resend Activation Link';
        this.buttonOption.active = true;

        this.authService.resendConfirmationEmailByEmail(
            this.form.get('email').value
        )
            .subscribe(
                data => {
                    const snackBarRef = this.snackBar.open(data.detail, 'OK');

                    snackBarRef.onAction().subscribe(() => {
                        this.router.navigate(['/auth/login'], { replaceUrl: true });
                    });

                    this.buttonOption.text = this.translations['auth.login.login_button'];
                    this.buttonOption.active = false;
                },
                error => {
                    this.showErrorMessage(error);
                }
            );

    }

}
